import React from 'react';
import BarChartItem from '../BarChartItem';
import TableItem from '../Table';
import snakeToPascal from '../../../tools';

interface OverviewTabProps {
  summary: any;
}

// Format IRR as percentage (value is a decimal like 0.1234 → "12.34%")
function formatIRR(value: any): string {
  if (value == null || value === 'NaN') return '-';
  const pct = (Number(value) * 100);
  return `${pct.toFixed(2)}%`;
}

// Compact info card for company/asset details
function InfoCard({ data, title }: { data: Record<string, any>; title?: string }) {
  // Séparer les champs KPI des champs textuels
  const kpiKeys = ['levered_irr'];
  const infoEntries = Object.entries(data).filter(([key]) => !kpiKeys.includes(key));
  const hasKpis = kpiKeys.some(k => data[k] != null);

  return (
    <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
      {title && <h5 className="text-sm font-semibold text-slate-700 mb-3">{title}</h5>}
      <div className="space-y-2">
        {infoEntries.map(([key, value]) => (
          <div key={key} className="flex justify-between items-center text-sm">
            <span className="text-slate-500">{snakeToPascal(key)}</span>
            <span className="font-medium text-slate-900">{value ?? '-'}</span>
          </div>
        ))}
      </div>

      {/* KPIs IRR */}
      {hasKpis && (
        <div className="mt-3 pt-3 border-t border-slate-200">
          <div className="bg-white rounded-md p-2.5 border border-slate-100 text-center">
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Levered IRR</p>
            <p className="text-base font-bold text-slate-900 mt-0.5">{formatIRR(data.levered_irr)}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function OverviewTab({ summary }: OverviewTabProps) {
  if (!summary) return null;

  return (
    <div className="space-y-6 min-w-0">
      {/* Project KPIs - Compact row */}
      <section className="bg-white rounded-xl border border-slate-200 p-4 overflow-hidden">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-primary text-[20px]">folder_open</span>
            </div>
            <div>
              <p className="text-lg font-bold text-slate-900">{summary.project_name || '-'}</p>
              <p className="text-xs text-slate-500">Project Name</p>
            </div>
          </div>

          <div className="h-8 w-px bg-slate-200 hidden sm:block" />

          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-slate-400 text-[18px]">apartment</span>
              <span className="text-sm"><strong>{summary.company_number || 0}</strong> Companies</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-slate-400 text-[18px]">factory</span>
              <span className="text-sm"><strong>{summary.asset_number || 0}</strong> Assets</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-slate-400 text-[18px]">date_range</span>
              <span className="text-sm">{summary.project_beginning || '-'} — {summary.project_ending || '-'}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Companies & Assets Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Companies */}
        {summary.companies_informations && summary.companies_informations.length > 0 && (
          <section className="bg-white rounded-xl border border-slate-200 p-5 overflow-hidden">
            <div className="flex items-center gap-2 mb-4">
              <span className="material-symbols-outlined text-slate-600 text-[20px]">apartment</span>
              <h3 className="text-base font-semibold text-slate-900">Companies</h3>
            </div>
            <div className="space-y-3">
              {summary.companies_informations.map((company: any, index: number) => (
                <InfoCard key={index} data={company} />
              ))}
            </div>
          </section>
        )}

        {/* Assets Info */}
        {summary.assets_informations && summary.assets_informations.length > 0 && (
          <section className="bg-white rounded-xl border border-slate-200 p-5 overflow-hidden">
            <div className="flex items-center gap-2 mb-4">
              <span className="material-symbols-outlined text-slate-600 text-[20px]">factory</span>
              <h3 className="text-base font-semibold text-slate-900">Assets</h3>
            </div>
            <div className="space-y-3">
              {summary.assets_informations.map((asset: any, index: number) => (
                <InfoCard key={index} data={asset} />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Asset Charts */}
      {(summary.asset_lifetime_chart_data || summary.asset_in_operation_chart_data) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {summary.asset_lifetime_chart_data && (
            <BarChartItem
              name="Asset(s) Lifetime"
              x_axis={summary.asset_lifetime_chart_data.x_axis}
              data={summary.asset_lifetime_chart_data.data}
              graduation_one_step_y={true}
              x_axis_string={true}
            />
          )}
          {summary.asset_in_operation_chart_data && (
            <BarChartItem
              name="Asset(s) in Operation Over Time"
              x_axis={summary.asset_in_operation_chart_data.x_axis}
              data={summary.asset_in_operation_chart_data.data}
              graduation_one_step_y={true}
            />
          )}
        </div>
      )}

      {/* Assets Key Information Table */}
      {summary.assets_key_informations && (
        <section className="bg-white rounded-xl border border-slate-200 p-5 overflow-hidden">
          <div className="flex items-center gap-2 mb-4">
            <span className="material-symbols-outlined text-slate-600 text-[20px]">info</span>
            <h3 className="text-base font-semibold text-slate-900">Assets Key Information</h3>
          </div>
          <TableItem table={summary.assets_key_informations} head_bold={true} bold_column_indexes={[0]} />
          {summary.assets_key_informations_percents && (
            <div className="mt-4">
              <TableItem table={summary.assets_key_informations_percents} head_bold={true} bold_column_indexes={[0]} />
            </div>
          )}
        </section>
      )}
    </div>
  );
}
